import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "src/context/AuthContext";
import { usePostAuthFlow } from "src/hooks/usePostAuthFlow";
import { useToast } from "src/hooks/use-toast";
import { ROUTES } from "src/routes/RouteConfig";

const ConfirmationCallback = () => {
  const navigate = useNavigate();
  const { isInitialized, user } = useAuth();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const source = searchParams.get("source");
  const oauthUserType = searchParams.get("oauth_usertype");
  const isOAuthSignupCallback = !!oauthUserType;
  
  // Also check if this is an OAuth login (existing user) by looking at the session provider
  const isOAuthLogin = user?.app_metadata?.provider && 
    user.app_metadata.provider !== 'email';
  
  // Either OAuth signup (new user) or OAuth login (existing user)
  const isOAuthFlow = isOAuthSignupCallback || isOAuthLogin;

  // Use the new post-auth flow hook
  const {
    handleOAuthFlow,
    handleAuthError,
  } = usePostAuthFlow();

  useEffect(() => {
    // Debug: Log URL information for OAuth flows
    if (isOAuthFlow) {
      console.log("OAuth flow URL debug:", {
        fullUrl: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        oauthUserType,
        isOAuthSignupCallback,
        isOAuthLogin,
        provider: user?.app_metadata?.provider,
      });
    }

    // For OAuth flows, wait for both initialization AND user to be available
    if (isOAuthFlow) {
      if (!isInitialized) {
        console.log("OAuth flow detected, waiting for AuthContext initialization...");
        return;
      }

      if (!user) {
        console.log("OAuth flow detected, AuthContext initialized but no user yet, waiting...");
        return;
      }
    }

    // Process OAuth flows only - email confirmations are handled by AuthContext
    const handleCallback = async () => {
      try {
        // Handle OAuth flow (both signup and login) using the new service
        if (isOAuthFlow && user) {
          console.log("Processing OAuth flow for user:", {
            userId: user.id,
            provider: user.app_metadata?.provider,
            oauthUserType,
            isSignup: isOAuthSignupCallback,
            isLogin: isOAuthLogin,
          });
          
          const result = await handleOAuthFlow({
            user,
            // For existing users (login), oauthUserType will be null, 
            // but PostAuthService will use the stored userType from database
            oauthUserType: oauthUserType || null,
          });

          if (!result.success) {
            throw new Error(result.error || "OAuth flow failed");
          }

          console.log("OAuth flow completed successfully:", result.redirectPath);
          return;
        }

        // Email confirmations are handled entirely by AuthContext
        // No need to process them here
        if (!isOAuthFlow) {
          console.log("Email confirmation detected - will be handled by AuthContext");
          // Fallback: If AuthContext hasn't redirected after 3 seconds, do it here
          const fallbackTimeout = setTimeout(() => {
            toast({
              title: "Email Confirmed!",
              description: "Your email has been confirmed. You can now log in.",
              variant: "default",
              duration: 4000,
            });
            window.location.href = ROUTES.LOGIN.path;
          }, 3000);
          return () => clearTimeout(fallbackTimeout);
        }

        // Fallback for OAuth without user
        if (isOAuthFlow && !user) {
          console.error("OAuth flow detected but no user found in AuthContext");
          navigate(ROUTES.LOGIN.path);
          return;
        }
        
      } catch (error) {
        console.error("Error during OAuth callback processing:", error);
        handleAuthError(error);
      }
    };

    handleCallback();
  }, [
    navigate,
    source,
    isOAuthFlow,
    isOAuthSignupCallback,
    isOAuthLogin,
    isInitialized,
    user,
    oauthUserType,
    handleOAuthFlow,
    handleAuthError,
    toast,
  ]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4">
          {isOAuthFlow ? "Setting up your account..." : "Confirming your email..."}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          You will be redirected automatically.
        </p>
      </div>
    </div>
  );
};

export default ConfirmationCallback;